import React from "react";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { useTranslation } from "react-i18next";
import LicenceTable from "./LicenceTable";
import PriceCalculatorLayout from "./PriceCalculatorLayout";

function resolveUserId(getAuthUser) {
  let localUser = null;
  try {
    const raw = localStorage.getItem("user");
    if (raw) localUser = JSON.parse(raw);
  } catch (_e) {
    localUser = null;
  }

  const authUser = getAuthUser?.() || null;
  const finalUser = authUser || localUser || null;
  const rawId =
    finalUser?.userId ??
    finalUser?.id ??
    finalUser?._id ??
    finalUser?.user?.id ??
    finalUser?.user?._id ??
    null;
  const id = Number(rawId);
  return Number.isFinite(id) ? id : null;
}

export default function PriceCalculatorTypeTablePage() {
  const { t } = useTranslation(undefined, { useSuspense: false });
  const { getAuthUser } = useJumboAuth();
  const tableRootRef = React.useRef(null);

  const userId = resolveUserId(getAuthUser);
  const showLogo = userId === 1;
  const currentYear = new Date().getFullYear();
  const pricingUnitNote = t("pages.priceCalculator.typeTable.footerNote", {
    defaultValue: "Cena je za 1 kalendárny mesiac.",
  });

  const handlePrint = React.useCallback(() => {
    const root = tableRootRef.current;
    if (!root) return;

    // Print exactly what is currently displayed: capture the DOM table.
    const tableEl = root.querySelector("table");
    if (!tableEl) return;

    // Clone the table and force icon colors/sizing for printing.
    // Note: MUI colors are applied via Emotion-generated CSS in the main app,
    // which won't exist inside the iframe.
    const wrapper = document.createElement("div");
    wrapper.innerHTML = tableEl.outerHTML;
    const originalSvgs = tableEl.querySelectorAll("svg");
    const clonedSvgs = wrapper.querySelectorAll("svg");
    // Inline key computed text styles so HTML formatting matches on-screen view
    // even without MUI/Emotion styles inside the iframe.
    try {
      const origEls = tableEl.querySelectorAll("*");
      const cloneEls = wrapper.querySelectorAll("*");
      for (let i = 0; i < Math.min(origEls.length, cloneEls.length); i++) {
        const o = origEls[i];
        const c = cloneEls[i];
        // Skip SVG subtree; handled separately below.
        if (
          c.tagName === "svg" ||
          c.tagName === "path" ||
          c.tagName === "circle" ||
          c.tagName === "rect" ||
          c.tagName === "line" ||
          c.tagName === "polyline" ||
          c.tagName === "polygon" ||
          c.tagName === "ellipse"
        ) {
          continue;
        }
        const cs = window.getComputedStyle(o);
        c.style.color = cs.color;
        c.style.fontWeight = cs.fontWeight;
        c.style.fontStyle = cs.fontStyle;
        c.style.textDecoration = cs.textDecoration;
        c.style.fontSize = cs.fontSize;
        c.style.lineHeight = cs.lineHeight;
      }
    } catch (_e) {
      // Best effort only
    }
    const applySvgCurrentColor = (svgEl) => {
      // Ensure children use currentColor so setting `color` on the <svg> works.
      const nodes = svgEl.querySelectorAll(
        "path,circle,rect,line,polyline,polygon,ellipse",
      );
      nodes.forEach((n) => {
        const fill = n.getAttribute("fill");
        if (fill === null || fill === undefined || fill === "") {
          n.setAttribute("fill", "currentColor");
        } else if (String(fill).toLowerCase() !== "none") {
          n.setAttribute("fill", "currentColor");
        }
        const stroke = n.getAttribute("stroke");
        if (stroke && String(stroke).toLowerCase() !== "none") {
          n.setAttribute("stroke", "currentColor");
        }
      });
      svgEl.style.fill = "currentColor";
      svgEl.style.stroke = "currentColor";
    };
    for (let i = 0; i < Math.min(originalSvgs.length, clonedSvgs.length); i++) {
      const orig = originalSvgs[i];
      const clone = clonedSvgs[i];
      const testId = orig.getAttribute("data-testid") || "";
      // Force consistent icon colors in print (match UI semantics)
      if (testId.includes("CheckCircleOutline")) {
        clone.style.color = "#2C9E4B";
      } else if (testId.includes("HighlightOff")) {
        clone.style.color = "#DA1113";
      } else {
        // Fallback: preserve computed color when available
        const computed = window.getComputedStyle(orig);
        clone.style.color = computed.color;
      }
      applySvgCurrentColor(clone);
      clone.style.width = "0.84em";
      clone.style.height = "0.84em";
      clone.style.display = "block";
      clone.style.margin = "0 auto";
    }

    const iframe = document.createElement("iframe");
    iframe.setAttribute("title", "price-calculator-type-table-print");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;

    const headerHtml = showLogo
      ? `
        <div class="print-header">
          <img class="print-logo" src="/images/logo.png" alt="DataVision" />
          <div class="print-title">Typy licencie</div>
        </div>`
      : `
        <div class="print-header no-logo">
          <div class="print-title">Typy licencie</div>
        </div>`;

    doc.open();
    doc.write(`<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <link rel="preconnect" href="https://fonts.googleapis.com" />
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
    <link
      href="https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600;700&display=swap"
      rel="stylesheet"
    />
    <title>Typy licencie</title>
    <style>
      @page { size: A4 portrait; margin: 12mm; }
      * { box-sizing: border-box; }
      html, body { width: 100%; }
      body { margin: 0; padding-right: 1px; font-family: "Open Sans", Arial, sans-serif; color: #111827; font-weight: 400; }
      .print-header { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 10px; }
      .print-header.no-logo { justify-content: flex-end; }
      .print-logo { height: 22px; width: auto; }
      .print-title { font-size: 14px; font-weight: 400; text-align: right; }
      table { width: 100%; max-width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 10px; border: 1px solid #d1d5db; outline: 1px solid #d1d5db; outline-offset: -1px; }
      th, td { border: 1px solid #d1d5db; padding: 4px 6px; white-space: normal; word-break: break-word; }
      /* Keep text column readable, center icon columns */
      th:first-child, td:first-child { text-align: left; vertical-align: top; }
      th:not(:first-child), td:not(:first-child) { text-align: center; vertical-align: middle; }
      /* Make icon columns narrower and functionality column as wide as possible */
      th:not(:first-child), td:not(:first-child) { width: 46px; padding: 2px 3px; }
      th:first-child, td:first-child { padding: 4px 6px; }
      /* Bold only column headers (thead). Keep everything else regular. */
      thead th { font-weight: 700; }
      tbody th, tbody td { font-weight: 400; }
      /* Make MUI icons 30% smaller */
      .MuiSvgIcon-root { font-size: 0.84em !important; }
      table svg { width: 0.84em !important; height: 0.84em !important; }
      table td { line-height: 1.2; }
      table td svg { vertical-align: middle; }
      /* Force icon colors by data-testid (works even if inline styles are stripped) */
      svg[data-testid*="CheckCircleOutline"] { color: #2C9E4B !important; fill: currentColor !important; stroke: currentColor !important; }
      svg[data-testid*="HighlightOff"] { color: #DA1113 !important; fill: currentColor !important; stroke: currentColor !important; }

      .print-note {
        margin-top: 10px;
        font-size: 10px;
        color: #6b7280;
      }
    </style>
  </head>
  <body>
    ${headerHtml}
    ${wrapper.innerHTML}
    <div class="print-note">
      Všetky ceny sú uvedené bez DPH 23% a platia od 1.1.${currentYear}.
    </div>
    <div class="print-note">
      ${pricingUnitNote}
    </div>
  </body>
</html>`);
    doc.close();

    // Give the browser a moment to layout & load images before printing.
    setTimeout(() => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } finally {
        setTimeout(() => {
          try {
            document.body.removeChild(iframe);
          } catch (_e) {}
        }, 1000);
      }
    }, 250);
  }, [showLogo, currentYear, pricingUnitNote]);

  return (
    <PriceCalculatorLayout>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mt: 2, mb: 2 }}
      >
        <Typography variant="h2">
          {t("pages.priceCalculator.typeTable.title", {
            defaultValue: "Typy licencie",
          })}
        </Typography>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
        >
          {t("buttons.print", { defaultValue: "Tlačiť" })}
        </Button>
      </Stack>

      <Paper variant="outlined">
        <Box ref={tableRootRef}>
          <LicenceTable />
        </Box>
        <Box sx={{ px: 2, pb: 1.5, pt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {pricingUnitNote}
          </Typography>
        </Box>
      </Paper>
    </PriceCalculatorLayout>
  );
}
